import { arrow } from "@/designSystemIcons";
import { cn } from "@/lib/utils";

import { elipsis as elipsisIcon } from "../../../icons";

type PaginationProps = {
  currentPage: number;
  totalAmountOfPages: number;
  changePage: (page: number) => void;
};

const TOTAL_AMOUNT_OF_PAGE_COMPONENTS = 7;

const PreviousButton = ({ onClick }: { onClick: () => void }) => {
  return (
    <button
      className={cn(
        "px-2 mobile:h-full w-8 mobile:w-auto h-7 bg-gray-200 text-black rounded text-sm",
      )}
      onClick={onClick}
    >
      <div className="flex flex-row items-center">
        <div className="m-auto rotate-90 w-2 h-2">{arrow}</div>
        <span className="md:hidden">Previous</span>
      </div>
    </button>
  );
};

const NextButton = ({
  onClick,
  hidden,
}: {
  onClick: () => void;
  hidden: boolean;
}) => {
  return (
    <div className="w-14" key={"next"}>
      <button
        className={cn(
          "px-2 mobile:h-full w-8 mobile:w-auto h-7 bg-gray-200 text-black rounded text-sm",
          hidden && "hidden",
        )}
        onClick={onClick}
      >
        <div className="flex flex-row items-center">
          <span className="md:hidden">Next</span>
          <div className="m-auto -rotate-90 w-2 h-2">{arrow}</div>
        </div>
      </button>
    </div>
  );
};

const SwitchPageButton = ({
  page,
  currentPage,
  changePage,
}: {
  page: number;
  currentPage: number;
  changePage: (page: number) => void;
}) => (
  <button
    key={page}
    className={cn(
      "w-8 h-7 text-center rounded text-sm",
      currentPage === page
        ? "text-white bg-primary-blue"
        : "bg-gray-200 text-black ",
    )}
    onClick={() => changePage(page)}
  >
    {page}
  </button>
);

const Elipisis = () => (
  <div className="w-8 mt-auto">
    <div className="w-4 mx-auto text-gray-200">{elipsisIcon}</div>
  </div>
);

const Pagination = ({
  currentPage,
  totalAmountOfPages,
  changePage,
}: PaginationProps) => {
  const onClickNext = () => {
    changePage(currentPage + 1);
  };

  const onClickPrevious = () => {
    changePage(currentPage - 1);
  };

  const pageComponents = [];
  if (totalAmountOfPages > TOTAL_AMOUNT_OF_PAGE_COMPONENTS) {
    pageComponents[1] = (
      <SwitchPageButton
        key={currentPage - 1}
        page={currentPage - 1}
        currentPage={currentPage}
        changePage={changePage}
      />
    );
    pageComponents[2] = (
      <SwitchPageButton
        key={currentPage}
        page={currentPage}
        currentPage={currentPage}
        changePage={changePage}
      />
    );
    pageComponents[3] = (
      <SwitchPageButton
        key={currentPage + 1}
        page={currentPage + 1}
        currentPage={currentPage}
        changePage={changePage}
      />
    );
    if (totalAmountOfPages - currentPage > 3) {
      pageComponents[4] = <Elipisis key="elipsis1" />;
    } else {
      pageComponents[1] = (
        <SwitchPageButton
          key={totalAmountOfPages - 4}
          page={totalAmountOfPages - 4}
          currentPage={currentPage}
          changePage={changePage}
        />
      );
      pageComponents[2] = (
        <SwitchPageButton
          key={totalAmountOfPages - 3}
          page={totalAmountOfPages - 3}
          currentPage={currentPage}
          changePage={changePage}
        />
      );
      pageComponents[3] = (
        <SwitchPageButton
          page={totalAmountOfPages - 2}
          currentPage={currentPage}
          changePage={changePage}
        />
      );
      pageComponents[4] = (
        <SwitchPageButton
          key={totalAmountOfPages - 1}
          page={totalAmountOfPages - 1}
          currentPage={currentPage}
          changePage={changePage}
        />
      );
    }
    if (currentPage > 3) {
      pageComponents[0] = <Elipisis key="elipsis2" />;
    } else {
      pageComponents[0] = (
        <SwitchPageButton
          key={2}
          page={2}
          currentPage={currentPage}
          changePage={changePage}
        />
      );
      pageComponents[1] = (
        <SwitchPageButton
          key={3}
          page={3}
          currentPage={currentPage}
          changePage={changePage}
        />
      );
      pageComponents[2] = (
        <SwitchPageButton
          key={4}
          page={4}
          currentPage={currentPage}
          changePage={changePage}
        />
      );
      pageComponents[3] = (
        <SwitchPageButton
          key={5}
          page={5}
          currentPage={currentPage}
          changePage={changePage}
        />
      );
    }
  } else {
    for (let index = 2; index < totalAmountOfPages; index++) {
      pageComponents[index] = (
        <SwitchPageButton
          key={index}
          page={index}
          currentPage={currentPage}
          changePage={changePage}
        />
      );
    }
  }

  return (
    <div className="flex flex-row text-xs gap-x-1 md-sm:gap-x-2 web-app:mr-1 md-sm:text-base mb-6 touch-device:mb-0">
      {currentPage > 1 && <PreviousButton onClick={onClickPrevious} key={0} />}
      <SwitchPageButton
        key={1}
        page={1}
        currentPage={currentPage}
        changePage={changePage}
      />
      {pageComponents}
      {totalAmountOfPages > 1 && (
        <SwitchPageButton
          key={totalAmountOfPages}
          page={totalAmountOfPages}
          currentPage={currentPage}
          changePage={changePage}
        />
      )}
      <NextButton
        key={totalAmountOfPages + 2}
        onClick={onClickNext}
        hidden={totalAmountOfPages === 0 || currentPage === totalAmountOfPages}
      />
    </div>
  );
};

export default Pagination;
